import type { ImageGalleryCategory, ImageGalleryImage } from "@/components/common/gallery";
import type { MediaItem, SUPType } from "@/types/sup";
import { unitSpaces } from "@/utils/sup/sort-media-list";

export interface GalleryProps {
	coverImage?: string;
	mediaList?: MediaItem[];
	projectId?: string;
	projectName?: string;
	className?: string;
	type?: SUPType;
	isLoading?: boolean;
	shouldPrioritizeCoverImage?: boolean;
	sizes?: string;
	onMainImageClick?: () => void;
	categories?: ImageGalleryCategory[];
}

export const defaultCategories: ImageGalleryCategory[] = [
	{ name: "Videos", code: "videos" },
	{ name: "Hall", code: "halls" },
	{ name: "Bedroom", code: "bedrooms" },
	{ name: "Balcony", code: "balconies" },
	{ name: "Kitchen", code: "kitchens" },
	{ name: "Amenities", code: "amenities" },
	{ name: "Bathroom", code: "bathrooms" },
	{ name: "Others", code: "others" },
	{ name: "Parking", code: "parkings" },
	{ name: "Pooja Room", code: "poojaRooms" },
	{ name: "Utility", code: "utilities" },
];

/**
 * Filter categories to only those with images, then sort by unitSpaces priority.
 */
export function getSortedCategories(
	categories: ImageGalleryCategory[] | undefined,
	images: ImageGalleryImage[],
): ImageGalleryCategory[] {
	return (categories || defaultCategories)
		.filter(cat => images.some(img => img.space === cat.code))
		.sort((a, b) => {
			const getCategoryPriority = (category: ImageGalleryCategory): number => {
				// Videos always come first
				if (category.code === "videos") return -1;
				const index = unitSpaces.indexOf(category.code);
				if (index !== -1) return index;
				return 1000;
			};

			const priorityA = getCategoryPriority(a);
			const priorityB = getCategoryPriority(b);

			if (priorityA !== priorityB) {
				return priorityA - priorityB;
			}

			return a.name.localeCompare(b.name);
		});
}

/**
 * Filter out items with the 'hidden' tag from mediaList.
 */
export function filterHiddenMedia(mediaList?: MediaItem[]): MediaItem[] {
	if (!mediaList) return [];
	return mediaList.filter(item => !item.tags?.includes("hidden"));
}
